import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  ChevronDown,
  ExternalLink,
  FolderOpen,
  Users,
} from "lucide-react";
import PublicHeader from "@/components/common/PublicHeader";
import { api } from "@/api/client";

const PRIMARY = "#1B5189" as const;

type Stakeholder = {
  id: string;
  nama: string;
  tipe: string;
  deskripsi: string | null;
  parent_id: string | null;
};

type StakeholderNode = Stakeholder & { children: Stakeholder[] };

// Build parent → children tree from flat list
function buildTree(list: Stakeholder[]): StakeholderNode[] {
  const childrenOf: Record<string, Stakeholder[]> = {};
  const roots: Stakeholder[] = [];

  for (const s of list) {
    if (s.parent_id) {
      if (!childrenOf[s.parent_id]) childrenOf[s.parent_id] = [];
      childrenOf[s.parent_id].push(s);
    } else {
      roots.push(s);
    }
  }

  return roots.map((r) => ({ ...r, children: childrenOf[r.id] ?? [] }));
}


// ─── Accordion row ────────────────────────────────────────────────────────────

function StakeholderRow({
  node,
  defaultOpen,
}: {
  node: StakeholderNode;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const hasChildren = node.children.length > 0;

  return (
    <div>
      {/* Row header */}
      <div className="flex items-center hover:bg-slate-50 transition-colors">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex flex-1 items-center gap-4 px-6 py-4 text-left"
        >
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
            <Building2 className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-bold text-slate-900 truncate">
                {node.nama}
              </span>
              {hasChildren && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 flex-shrink-0">
                  {node.children.length} Sub-stakeholder
                </span>
              )}
            </div>
            {node.deskripsi && (
              <p className="text-xs text-slate-500 mt-0.5 truncate">{node.deskripsi}</p>
            )}
          </div>
          {hasChildren && (
            <ChevronDown
              className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            />
          )}
        </button>

        {/* Link ke halaman PNBP filter by stakeholder ini */}
        <Link
          to={`/pnbp?stakeholder=${node.id}`}
          className="flex items-center gap-1.5 px-4 py-2 mr-3 rounded-lg text-xs font-semibold text-white transition hover:opacity-90 flex-shrink-0"
          style={{ backgroundColor: PRIMARY }}
          title={`Lihat PNBP ${node.nama}`}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Lihat PNBP</span>
        </Link>
      </div>

      {/* Children */}
      {hasChildren && open && (
        <div className="border-t border-slate-100 bg-slate-50/60">
          <ul className="py-2 pl-[72px] pr-6 space-y-0.5">
            {node.children.map((child) => (
              <li key={child.id} className="flex items-center gap-2">
                <div className="flex flex-1 items-center gap-3 py-2 px-3 rounded-lg">
                  <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-blue-100">
                    <Building2 className="h-3.5 w-3.5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-slate-700 truncate block">
                      {child.nama}
                    </span>
                    {child.deskripsi && (
                      <span className="text-xs text-slate-400 truncate block">{child.deskripsi}</span>
                    )}
                  </div>
                </div>
                <Link
                  to={`/pnbp?stakeholder=${child.id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition hover:opacity-90 flex-shrink-0"
                  style={{ backgroundColor: PRIMARY }}
                >
                  <ExternalLink className="h-3 w-3" />
                  <span className="hidden sm:inline">Lihat PNBP</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function StakeholderPage() {
  const { data: stakeholderList = [], isLoading } = useQuery<Stakeholder[]>({
    queryKey: ["public", "stakeholder"],
    queryFn: () => api.get<Stakeholder[]>("/public/stakeholder"),
    staleTime: 10 * 60 * 1000,
  });

  const tree = buildTree(stakeholderList);
  const totalChildren = stakeholderList.filter((s) => s.parent_id !== null).length;

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <PublicHeader />

      {/* ─── Hero ───────────────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-6 pt-12">
        <div
          className="relative overflow-hidden rounded-3xl px-8 py-16 text-center shadow-xl"
          style={{
            background: "linear-gradient(135deg, #2DD4BF 0%, #1B5189 50%, #0F2547 100%)",
          }}
        >
          <div className="relative z-10 mx-auto max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-medium text-cyan-300 backdrop-blur">
              <Users className="h-3.5 w-3.5" />
              Ekosistem PNBP
            </span>
            <h1 className="mt-5 text-4xl font-bold text-white sm:text-5xl">
              Daftar Stakeholder PNBP
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-base text-blue-100">
              Seluruh pemangku kepentingan dalam ekosistem Penerimaan Negara
              Bukan Pajak, lintas kementerian dan lembaga.
            </p>
          </div>
        </div>
      </section>

      {/* ─── Stats strip ─────────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-6 py-8">
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5 text-center shadow-sm">
            <div className="text-3xl font-bold text-slate-900">{stakeholderList.length}</div>
            <div className="mt-1 text-xs text-slate-500 font-medium">Total Stakeholder</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 text-center shadow-sm">
            <div className="text-3xl font-bold text-slate-900">{tree.length}</div>
            <div className="mt-1 text-xs text-slate-500 font-medium">Induk</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 text-center shadow-sm">
            <div className="text-3xl font-bold text-slate-900">{totalChildren}</div>
            <div className="mt-1 text-xs text-slate-500 font-medium">Sub-stakeholder</div>
          </div>
        </div>
      </section>

      {/* ─── Accordion list ─────────────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        <div className="overflow-hidden rounded-2xl border-2 border-blue-200 bg-white shadow-sm">
          {/* Card header */}
          <div className="flex items-center border-b border-slate-100 bg-slate-50/50 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                <FolderOpen className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold text-slate-900">Stakeholder PNBP</span>
                  <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-600">
                    {stakeholderList.length} Entitas
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  Klik baris untuk expand sub-stakeholder, atau "Lihat PNBP" untuk melihat dokumen.
                </p>
              </div>
            </div>
          </div>

          {/* List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-3">
              <div className="animate-spin h-5 w-5 rounded-full border-b-2 border-blue-600" />
              Memuat data stakeholder…
            </div>
          ) : tree.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-slate-400">
              <Building2 className="h-10 w-10 mb-3" />
              <p className="text-sm">Belum ada data stakeholder.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {tree.map((node, idx) => (
                <StakeholderRow key={node.id} node={node} defaultOpen={idx === 0} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
